import { Box, Card, IconButton, CardContent, Stack, Typography, Button, Dialog, useMediaQuery, Drawer, Tooltip, SvgIcon } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import CertificationDetails from './AddCertification';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import dayjs from 'dayjs';
import RemoveCertificate from './RemoveCertificateDialog';
import { useSnackbar } from 'components/snackbar';
import { deleteCertificateHistory } from 'api/users';
import { endpoints } from 'utils/axios';
import AddCertificationDrawer from 'components/basic-details-mobile-ui/AddCertificationDrawer';
import { allCapitalize } from 'utils/Utils';
import { useTheme } from '@mui/material/styles';
import InfoNote from './InfoNote';
import { EditSvgIcon, DeleteSvg } from 'components/icon/icon';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';

/**
 * CertificationSection component
 * @param {object} certificateData
 * @param {function} onCancel
 * @param {object} expandedSection
 * @param {function} handleExpand
 * @param {function} onCertificateDataChange
 * @param {object} educationData
 * @param {object} workExperienceData
 * @param {boolean} showCert
 * @param {function} handleShowCert
 * @param {function} handleDeleteCert
 * @returns {ReactElement}
 */
const CertificationSection = ({ certificateData, onCancel, expandedSection, handleExpand, onCertificateDataChange, educationData, workExperienceData, showCert, handleShowCert, handleDeleteCert }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showEducation, setShowEducation] = useState(false);
  const [educations, setEducations] = useState([]);
  const [uploaded, setUploaded] = useState(false);
  const [actionType, setActionType] = useState('');
  const [open, setOpen] = useState(false);
  const [itemId, setItemId] = useState('');
  const [title, setTitle] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const [selectedCard, setSelectedCard] = useState(null);
  const certificationIcon = `${process.env.REACT_APP_HOMEPAGE}assets/certification-img.png`;
  const [idForScroll, setIdForScroll] = useState(null);
  const [scroll, setScroll] = useState(false);

  const certificationRef = useRef(null);
  const certificateSectionRef = useRef(null);
  const detailsRef = useRef(null);

  const [certificateInstituteData, setCertificateInstituteData] = useState([]);
  const [certificateTitleData, setCertificateTitleData] = useState([]);

  // const [openDetailsDrawer, setOpenDetailsDrawer] = useState(false);
  const [selected, setSelected] = useState({});
  const [isReadOnly, setReadOnly] = useState(showCert ? true : false);

  useEffect(() => {
    /**
     * Fetches data for certificate institutes and titles.
     * This function is called when the component mounts to retrieve
     * necessary data for the certification section, ensuring that
     * institute and title data are available for use.
     */

    const fetchData = async () => {
      await fetchCertificateInstituteData();
      await fetchCertificateTitle();
    }
    fetchData();
  }, []);

  useEffect(() => {
    const newWork = certificateData?.filter(data =>
      !educations?.some(history => history._id === data._id)
    );
    if (certificateData && certificateData?.length > 0) setIdForScroll(newWork[0]?._id || null);
    setEducations(certificateData);
  }, [certificateData, uploaded]);

  useEffect(() => {
    if (idForScroll && scroll) {
      scrollToChild(idForScroll);
      setIdForScroll(null);
    }
  }, [idForScroll]);

  useLayoutEffect(() => {
    if (expandedSection?.section === 'certificate' && detailsRef?.current) {
      detailsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [detailsRef, expandedSection]);

  /**
   * Scrolls to a specific child element with the given targetId.
   * If the element exists, it will be scrolled into view smoothly.
   * @param {string} targetId the id of the element to scroll to
   */
  const scrollToChild = (targetId) => {
    if (targetId) {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }
  };

  /**
   * Scrolls the certificate section to the top position of the certification reference.
   * This function allows smooth scrolling to the certification element within the section,
   * enhancing user navigation experience by bringing the certification area into view.
   */

  const scrollToEducation = () => {
    certificateSectionRef.current.scrollTo({
      top: certificationRef.current.offsetTop,
      behavior: 'smooth',
    });
  };

  /**
   * Handles the click event on a certification card.
   * Expands or collapses the corresponding card section and
   * sets the selected card and read-only state if applicable.
   * For mobile devices, it also opens the Details drawer.
   * @param {object} item the certification data
   * @param {number} index the index of the certification card
   */
  const handleCardClick = (item, index) => {
    handleExpand('certificate', index);
    setSelectedCard(item);
    if (isMobile) {
      setSelected(item);
      handleOpenDrawer();
    } else {
      setShowEducation(false);
    }
    item?.status !== 'active' && setReadOnly(false);
  };

  /**
   * Handles the deletion of a certification. If the certificationId is null, removes the certification from local storage and filters it out from the educations array. If the certificationId is not null, opens the Remove dialog box.
   * @param {string} certificateId the id of the certification to be deleted
   * @param {string} title the title of the certification to be deleted
   * @param {Event} event the event object
   */
  const handleDeleteCertificate = async (certificateId, title, event) => {
    event.stopPropagation();
    if (!certificateId) {
      localStorage.removeItem("certificate");
      setEducations([...educations?.filter(edu => edu?._id)]);
      setSelectedCard(null);
    } else {
      setActionType('certificate');
      setTitle(title);
      setItemId(certificateId);
      handleOpenRemove();
    }
  };

  /**
   * Handles the data change of a certification.
   * If the certification has changed, updates the certification data in the parent component
   * and sets the scroll flag to true. If the certification has not changed, just closes the
   * Details drawer and sets the showEducation flag to false.
   * @param {boolean} isChanged - whether the certification has changed
   */
  const handleDataChange = async (isChanged, certificateId) => {

    handleCloseDrawer();
    setShowEducation(false);
    if (isChanged) {
      await onCertificateDataChange();
      setScroll(true);
      if (certificateId) setIdForScroll(certificateId);
    }
  };


  /**
   * Closes the Remove dialog box and resets the itemId state variable.
   */

  const handleClose = () => {
    handleCloseRemove();
    setItemId('');
  };
  /**
   * Handles the deletion of a certificate. If the showCert flag is true, calls the handleDeleteEdu function and closes the dialog. Otherwise, calls the deleteCertificateHistory function and closes the dialog if the response is successful.
   */
  const handleDeleteAction = async () => {
    if (showCert) {
      handleDeleteEdu(itemId);
      return;
    }
    if (actionType === 'certificate') {
      const response = await deleteCertificateHistory({ certificateId: itemId });
      if (response && response?.success) {
        setEducations(response?.data || []);
        onCertificateDataChange();
        handleCloseDrawer();
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    }
    handleClose();
  };
  /**
   * Handles the addition of a new certificate. Sets showEducation to true and calls the handleExpand function with the type 'certificate' and the length of the educations array as the index. It also resets the selectedCard state variable to an empty object.
   */
  const handleAddNewEdu = () => {
    setShowEducation(true);
    handleExpand("certificate", educations.length);
    setSelectedCard({});
  };
  /**
   * Fetches certificate institute data from the API.
   * The endpoint is constructed using the base endpoint and the `certificateIssueBy` service path.
   * The response data is deduplicated and stored in the state as a list of unique certificate institutes.
   * Uses the environment variable for the base endpoint.
   */

  const fetchCertificateInstituteData = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.certificateIssueBy}`);
    const resultData = await response.json();
    setCertificateInstituteData([...new Set([...resultData])]);
  };
  /**
   * Fetches certificate title data from the API.
   * The endpoint is constructed using the base endpoint and the `certificate` service path.
   * The response data is deduplicated and stored in the state as a list of unique certificate titles.
   * Uses the environment variable for the base endpoint.
   */
  const fetchCertificateTitle = async () => {
    const response = await fetch(`${process.env.REACT_APP_END_POINT}${endpoints?.services?.certificate}`);
    const resultData = await response.json();
    setCertificateTitleData([...new Set([...resultData])]);
  };
  /**
   * Checks for overlapping periods between the provided data and existing education, certificate, and work experience records.
   * It filters out the current certificate from the list and checks for overlaps in education, certificates, and work experiences.
   * If an overlap is found, it returns an object indicating the overlap type and degree/certificate.
   *
   * @param {Object} data - The data object containing the period to check for overlaps.
   * @returns {Object} - An object with a boolean indicating if there is an overlap, and details about the overlap type and associated degree/certificate.
   */

  const checkingOverlapping = (data) => {
    if (data?.learning_format !== "Full time - offline") return { isOverlapping: false };
    /**
     * Checks for overlapping periods between the provided items and a given date range.
     * Iterates over the items to determine if any item's date range overlaps with the
     * specified data range. The overlap check considers both the start and end dates.
     *
     * @param {Array} items - An array of objects containing date ranges to check for overlap.
     * @param {string} fromKey - The key in the item object representing the start date.
     * @param {string} toKey - The key in the item object representing the end date.
     * @param {string} typeLabel - A label indicating the type of overlap (e.g., 'work experience').
     * @param {string} degreeKey - The key in the item object representing the degree or title of the item.
     * @returns {Object} - An object indicating whether there is an overlap, along with the type and associated degree.
     */

    const checkOverlap = (items, fromKey, toKey, typeLabel, degreeKey) => {
      for (let i = 0; i < items.length; i++) {
        const fromDate = new Date(items[i][fromKey]);
        const toDate = typeLabel === 'work experience' ? (items[i]?.present ? new Date() : new Date(items[i][toKey])) : new Date(items[i][toKey]);
        const dataFromDate = new Date(data?.education_from);
        const dataToDate = new Date(data?.education_to);
        if (fromDate <= dataToDate && dataFromDate <= toDate) {
          return { isOverlapping: true, type: typeLabel, degree: items[i][degreeKey] };
        }
      }
      return { isOverlapping: false };
    };

    const filterEducation = educationData?.filter(edu => edu?._id !== data?._id && edu?.learning_format === "Full time - offline");
    const educationOverlap = checkOverlap(filterEducation, 'education_from', 'education_to', 'education', 'degree_received');
    if (educationOverlap.isOverlapping) return educationOverlap;

    const filterCertificate = certificateData?.filter(edu => edu?._id !== data?._id && edu?.learning_format === "Full time - offline");
    const certificateOverlap = checkOverlap(filterCertificate, 'education_from', 'education_to', 'certificate', 'certificate_received');
    if (certificateOverlap.isOverlapping) return certificateOverlap;

    const filterWorkExperienceData = workExperienceData?.filter(edu => edu?._id !== data?._id && edu?.organization_name !== 'Work Gap');
    const workOverlap = checkOverlap(filterWorkExperienceData, 'work_history_from', 'work_history_to', 'work experience', 'last_job_function');
    if (workOverlap.isOverlapping) return workOverlap;

    return { isOverlapping: false };
  };
  /**
   * Handles when the user selects a certificate to view in the resume form.
   * Removes any previously stored certificate id from local storage, closes the education drawer,
   * closes the details drawer, enables scrolling, and calls the handleShowCert function.
   * @param {Object} certificate - The certificate object to be passed to handleShowCert.
   */
  const handleEducationResume = (certificate, id) => {
    localStorage.removeItem('certificate');
    setShowEducation(false);
    handleCloseDrawer();
    setScroll(true);
    if (id) setIdForScroll(id);
    handleShowCert('certificate', certificate);
  };
  /**
   * Handles when the user selects a work experience to view in the resume form.
   * Stores the selected work experience in the selectedCard state variable, opens the details drawer, and enables read only mode if the status of the work experience is not 'active'.
   * @param {Object} work - The work experience object to be passed to the details drawer.
   */
  const handleOpenWorkDrawer = (work) => {
    setSelected(work);
    handleOpenDrawer();
    item?.status !== 'active' && setReadOnly(false);
  };
  /**
   * Handles the deletion of an education item in the resume form.
   * Calls the handleDeleteCert function to delete the education item from the server,
   * and then closes the education drawer and the modal dialog.
   * @param {string} eduId - The id of the education item to be deleted.
   */
  const handleDeleteEdu = (eduId) => {
    handleDeleteCert(eduId);
    // onEducationDataChange(); 
    handleCloseDrawer();
    handleClose();
  };

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openDetailsDrawer = dialogIds.includes("certification");
  const openRemove = dialogIds.includes("removeCertificate");

  useEffect(() => {
    if (!openDetailsDrawer) {
      setShowEducation(false);
      setSelectedCard(null);
      onCancel();
      showCert && setReadOnly(true);
      handleDataChange();
    }
  }, [openDetailsDrawer]);

  const handleCloseDrawer = () => {
    const timer = setTimeout(() => {
      closeDialog('certification');
    }, 300);
  };
  const handleOpenDrawer = () => {
    openDialog('certification');
  };
  const handleCloseRemove = () => {
    closeDialog('removeCertificate');
  };
  const handleOpenRemove = () => {
    openDialog('removeCertificate');
  };

  return (
    <Box className="candidate-basic-det-wrapper" sx={{ position: 'relative' }}>
      <Box mb={3}>
        <Typography className="step-heading xs-fs-16" variant="h6" gutterBottom>
          Your Certification (Optional)
        </Typography>
        <Typography className="step-info xs-fs-12" variant="body2" gutterBottom>
          Putting up relevant certifications help recruiters understand you better
        </Typography>
      </Box>
      <Box mb={3} ref={certificateSectionRef}>
        {educations?.length !== 0 &&
          educations?.map((item, index) => (
            <React.Fragment key={item?._id || index}>
              {expandedSection?.section !== 'certificate' || expandedSection?.index !== index ? (
                <Box className={showCert && "update-resume-accord-wrapper"}>
                  <Card id={idForScroll === item?._id ? idForScroll : null} className={`info-list-item ${item?.status !== "active" ? "incomplete-details" : ""}`} variant="outlined" key={item._id} sx={{ marginTop: 1 }}
                    onClick={() => handleCardClick(item, index)} >
                    <CardContent>
                      <Stack direction="row" justifyContent={'space-between'} spacing={2}>
                        <Box>
                          <Stack direction="row" alignItems={'center'} spacing={1.5}>
                            <Typography variant="h6" className="card-header-title">
                              {item?.certificate_received} at {allCapitalize(item?.education_institute_name)}
                            </Typography>
                            {item?.status !== "active" && <Box className="incomplete-tag">Incomplete</Box>}
                          </Stack>
                          <Stack direction="row" alignItems={'center'} mt={0.5} spacing={2}>
                            <Box
                              component="img"
                              alt="certification"
                              sx={{ marginRight: '5px!important' }}
                              src={certificationIcon}
                            />
                            <Typography className="info-wrapper" variant="body2" gutterBottom>
                              {item.education_to ? `${dayjs(item.education_to).format('MMM YYYY')}` : ''}
                            </Typography>
                          </Stack>
                        </Box>
                        <Stack direction="row" alignItems={'center'} spacing={2}>
                          {item?.status !== "active" &&
                            <IconButton
                              className="delete-sec-btn"
                              aria-label="delete"
                              onClick={(event) => handleDeleteCertificate(item._id, item.certificate_received, event)}
                            >
                              <DeleteSvg />
                            </IconButton>
                          }
                          <KeyboardArrowDownOutlinedIcon />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              ) : (
                <Box className={isReadOnly && "update-resume-accord-wrapper"} ref={detailsRef}>
                  <Card className={isReadOnly ? "info-list-item" : "form-wrapper"} variant="outlined">
                    <CardContent>
                      <CertificationDetails
                        key={item._id}
                        certificateData={educations.find((_, idx) => idx === expandedSection.index)}
                        id={item._id}
                        onCancel={() => {
                          setShowEducation(false);
                          setSelectedCard(null);
                          onCancel();
                          showCert && setReadOnly(true);
                        }}
                        handleArrowClick={() => {
                          onCancel();
                          showCert && setReadOnly(true);
                        }}
                        onCertificateDataChange={handleDataChange}
                        certificateInstituteData={certificateInstituteData}
                        certificateTitleData={certificateTitleData}
                        checkingOverlapping={checkingOverlapping}
                        showCert={showCert}
                        handleShowCert={handleEducationResume}
                        handleDeleteEdu={handleDeleteEdu}
                        isReadOnly={isReadOnly}
                        setReadOnly={setReadOnly}
                      />
                    </CardContent>
                  </Card>
                </Box>
              )}
            </React.Fragment>
          ))}
      </Box>
      {
        (showEducation && expandedSection.section === 'certificate' &&
          (<Box className={isReadOnly && "update-resume-accord-wrapper"} ref={detailsRef}>
            <Card className={isReadOnly ? "info-list-item" : "form-wrapper"} variant="outlined">
              <CardContent>
                <CertificationDetails
                  onCertificateDataChange={handleDataChange}
                  certificateInstituteData={certificateInstituteData}
                  certificateTitleData={certificateTitleData}
                  checkingOverlapping={checkingOverlapping}
                  onCancel={() => {
                    setShowEducation(false);
                    setSelectedCard(null);
                    onCancel();
                    showCert && setReadOnly(true);
                  }}
                  handleArrowClick={() => {
                    onCancel();
                    showCert && setReadOnly(true);
                  }}
                  showCert={showCert}
                  handleShowCert={handleEducationResume}
                  handleDeleteEdu={handleDeleteEdu}
                  isReadOnly={isReadOnly}
                  setReadOnly={setReadOnly}
                />
              </CardContent>
            </Card>
          </Box>
          ))}
      <Box mt={2}>
        {(!selectedCard || selectedCard?.status || expandedSection?.section !== 'certificate') &&
          <Button
            className="add-btn"
            disableRipple
            startIcon={<AddIcon />}
            disabled={educations?.some(edu => !edu?._id)} onClick={() => {
              if (isMobile) {
                setShowEducation(true);
                handleExpand("certificate", educations.length);
                setSelectedCard({});
                setSelected(null);
                handleOpenDrawer();
              } else {
                handleAddNewEdu();
              }
              setReadOnly(false);
            }}>
            Add Certification
          </Button>}
      </Box>

      {isMobile ?
        <Drawer
          anchor="bottom"
          open={openRemove}
          onClose={handleClose}
          className="forgot-password-dialog otp-dialog-wrapper"
        >
          <RemoveCertificate onClose={handleClose} onRemove={handleDeleteAction} title={title} />
        </Drawer> :
        <Dialog
          open={openRemove}
          onClose={handleClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="otp-dialog-wrapper"
        >
          <RemoveCertificate onClose={handleClose} onRemove={handleDeleteAction} title={title} />
        </Dialog>}

      <Drawer className="mob-side-panel" open={openDetailsDrawer} anchor={'right'} >
        <AddCertificationDrawer
          onCertificateDataChange={handleDataChange}
          certificateInstituteData={certificateInstituteData}
          certificateTitleData={certificateTitleData}
          checkingOverlapping={checkingOverlapping}
          onCancel={() => {
            setShowEducation(false);
            setSelectedCard(null);
            localStorage.removeItem('certificate');
            onCancel();
            // handleCloseDrawer();
            showCert && setReadOnly(true);
          }}
          handleArrowClick={() => {
            handleDataChange(true);
            // handleCloseDrawer();
            onCancel();
            showCert && setReadOnly(true);
          }}
          certificateData={selected}
          id={selected?._id}
          selected={selected}
          handleDeleteCertificate={handleDeleteCertificate}
          showCert={showCert}
          handleDeleteEdu={handleDeleteEdu}
          isReadOnly={isReadOnly}
          setReadOnly={setReadOnly}
          handleShowCert={handleEducationResume}
          deleteIncompleteSection={() => {
            setShowEducation(false);
            setSelectedCard(null);
            localStorage.removeItem('certificate');
            onCancel();
            handleCloseDrawer();
            showCert && setReadOnly(true);
          }}
        />
      </Drawer>
    </Box>
  );
};

export default CertificationSection;
